import React from 'react';
import { required, SimpleForm, Edit, Create, EditButton, List, Datagrid, TextField,
  TextInput, ReferenceField, ReferenceInput, ReferenceArrayInput, AutocompleteArrayInput, AutocompleteInput,
  ListActions, BooleanInput, SelectArrayInput, ArrayInput, SimpleFormIterator, FormDataConsumer, NumberInput,
  downloadCSV
} from 'react-admin';
import DeleteWithConfirmation from './components/DeleteWithConfirmation';
import EntityToolbar from './components/EntityTooolbar';
import { Accordion, AccordionSummary, AccordionDetails } from '@material-ui/core';
import { ExpandMore } from '@material-ui/icons';
import { ColorInput } from './components/utils';
import jsonExport from 'jsonexport/dist';
const prebidBidders = require('./prebidBidderModules.json');

const exporter = (data) => {
  const dataExport = data.map(data => (
      {
        Code: data.adunit_code,
        'Ad unit': `${data.adunit}`,
        'Publisher ID': data.publisher_id,
        'Publisher': data.publisher_name,
        'Site ID': data.site_id,
        'Site': data.site_name,
        'Is Video': data.isVideo ? 'Yes' : 'No',
        'Ad Sizes': data.sizeConfig ? data.sizeConfig.map(sz => `${sz.label}: [${sz.sizes ? sz.sizes.map(s => JSON.parse(s).join('x')) : ''}]`).join(', ') : '--',
        'Bidders': data.bids ? data.bids.map(b => `${b.bidder} (${b.params ? Object.entries(b.params).map(([k,v]) => `${k}:${v}`).join(', ') : ''})`).join(' | ') : '--',
        'Lazy Load': data.lazyLoad && data.lazyLoad.enabled ? `Yes (Margin: ${data.lazyLoad.margin || 0}px)` : 'No',
        'Sliding Window': data.slidingWindow && data.slidingWindow.enabled ? 'Yes' : 'No',
        'Auto Refresh': data.autorefresh && data.autorefresh.enabled ? `Yes (Threshold: ${data.autorefresh.threshold || 0}%, TTV: ${data.autorefresh.ttv || 0}s, Impressions Limit: ${data.autorefresh.impressionsLimit || 0}${data.autorefresh.stopLineItem ? ', Stop Line Item: ' + data.autorefresh.stopLineItem : ''})` : 'No'
      }
    )
  )
  jsonExport(dataExport, {
        headers: ['Code', 'Ad unit']
    }, (err, csv) => {
        downloadCSV(csv, 'adunits');
    });
};

const AD_SIZES = [[970, 250], [300, 100], [300, 600], [970, 90], [300, 50], [300, 250], [728, 90], [336, 280], [160, 600]
, [970, 66], [320, 50], [320, 100], [300, 1050], [120, 600], [480, 60], [250, 250], [468, 60], [200, 200], [375, 350]
, [320, 480], [300, 168], [300, 75], [120, 20], [120, 30], [216, 54], [234, 60], [216, 36], [168, 28], [120, 90], [120, 60]
, [88, 31], [900, 90], [180, 150], [320, 250], [120, 240], [125, 125], [580, 400], [150, 125], [1350, 17], [480, 320]
, [200, 150], [1, 1], [320,400], [768,300], [1200,300], [320,200], [728,200], [1200,600], [600,420]];

const getAdSizeChoices = (type) => {
  let c = AD_SIZES.sort((a, b) => a[0] - b[0] || a[1] - b[1]).map(x=>({id: JSON.stringify(x), name: `${x[0]}x${x[1]}`}));
  return c;
  // return c.filter(x=> Number(x.name.split('x')[0]) < SCREEN_SIZES_WIDTH_RANGE.find(x => x.name === type).maxSize);
}
const SCREEN_SIZES_WIDTH_RANGE = [
  {name: 'Mobile', maxSize: 767, id: '[0,0]'},
  {name: 'Tablet', maxSize: 992, id: '[767,0]'},
  {name: 'Desktop', maxSize: 2000, id: '[992,0]'}
];

const PrebidBidderSelector = ({source, scopedFormData}) => {
  const getIdentifiers = () => prebidBidders.find(x=>x.id === scopedFormData?.bidder)?.identifiers;
  const getIdentifiersOptional = () => prebidBidders.find(x=>x.id === scopedFormData?.bidder)?.identifiers_optional;
  const identifiers = getIdentifiers();
  const identifiersOptional = getIdentifiersOptional();
  let input = [];
  if (!['nobid', 'duration'].includes(scopedFormData?.bidder)) {
    if (identifiers) {
      identifiers.forEach (identifier => {
        input.push(<tr><td><TextInput source={`${source}.params.${identifier?.code || 'siteId'}`} label={`${identifier?.name} [${identifier?.code}] *` || 'Site ID'} fullWidth/></td></tr>);
      }); 
    }
    if (identifiersOptional) {
      identifiersOptional.forEach (identifier => {
        input.push(<tr><td><TextInput source={`${source}.params.${identifier?.code || 'siteId'}`} label={`${identifier?.name} [${identifier?.code}] (optional)` || 'Site ID'} fullWidth/></td></tr>);
      }); 
    }
  } else {
    if (identifiersOptional) {
      identifiersOptional.forEach (identifier => {
        input.push(<tr><td><TextInput source={`${source}.params.${identifier?.code || 'siteId'}`} label={`${identifier?.name} [${identifier?.code}] (optional)` || 'Site ID'} fullWidth/></td></tr>);
      }); 
    }
  }
  return (
      <div style={{display: 'flex', width: '100%', gap: '1px'}}>
        <table style={{width: '90%'}}>
          <tr><td><AutocompleteInput source={`${source}.bidder`} choices={prebidBidders} label='Bidder' /></td></tr>
          {input}
        </table>
      </div>
  )
}

// Per-breakpoint sliding window inputs. The breakpoint element is anchored by
// the hidden sizeConfig[index].label / minViewPort inputs in the Banner Ad
// Sizes box, so these nested sources bind even when slidingWindow is absent.
const SlidingWindowFields = ({ index, label }) => (
  <div style={{padding: '10px', display: 'flex', flexDirection: 'column', minWidth: '220px'}}>
    <strong>{label}</strong>
    <BooleanInput source={`sizeConfig[${index}].slidingWindow.enabled`} label='Enable Sliding Window'/>
    <NumberInput source={`sizeConfig[${index}].slidingWindow.width`} label='Width' style={{width: '60%'}} helperText='Width of the sliding window'/>
    <NumberInput source={`sizeConfig[${index}].slidingWindow.height`} label='Height' style={{width: '60%'}} helperText='Height of the sliding window'/>
    <ColorInput source={`sizeConfig[${index}].slidingWindow.backgroundColor`} label='Background color' helperText='Default "silver". Hex (#edf1ef) or named colors both work.'/>
  </div>
);

const validJSON = input => { if (input) { try { JSON.parse(input) } catch (e){ return "Invalid JSON: " + e} }};

const adunitFilters = [
	<TextInput label="Search" source="search_adunit_code" alwaysOn resettable />,
	<ReferenceArrayInput source="site_id" label="Site" 
		reference="sites"
		basePath='sites'
		alwaysOn
		perPage={10}
		sort={{ field: 'name', order: 'ASC' }}
		filterToQuery={searchText => ({ search_name: searchText })}
		allowEmpty>
		<AutocompleteArrayInput label="Sites" optionText={rec => rec && rec.name ? `${rec.name} (${rec.publisher_name})` : ''} optionValue="code" style={{ width: '90%' }} />
	</ReferenceArrayInput>,
	<ReferenceArrayInput source="publisher_id" label="Publisher"
		reference="publishers"
		basePath='publishers'
		perPage={10}
		sort={{ field: 'name', order: 'ASC' }}
		filterToQuery={searchText => ({ search_name: searchText })}>
		<AutocompleteArrayInput label="Publishers" optionText="name" optionValue="code" style={{ width: '90%' }} />
	</ReferenceArrayInput>
];
export const AdunitList = ({ permissions, ...props }) => {
return (
  <List {...props} filters={adunitFilters} perPage={25} actions={<ListActions />}
    exporter={exporter}
     >
    <Datagrid rowClick={null} >
      <TextField source="adunit_code" label="Code" />
      <TextField source="adunit" label="adunit" />
      <ReferenceField source="site_id" reference="sites">
        <TextField source="name" />
      </ReferenceField>
      <ReferenceField source="publisher_id" reference="publishers">
        <TextField source="name" />
      </ReferenceField>
      <EditButton variant="contained" color="primary" />
      <DeleteWithConfirmation {...props}/>
    </Datagrid>
  </List>
)
};

const AdunitTitle = ({ record }) => {
    return <span>Ad Unit {record ? `"${(record.code)}"` : ''}</span>;
};

export const TheForm = ({ permissions, formType, ...props }) => (
  <SimpleForm toolbar={<EntityToolbar /> } redirect={false} {...props} >
  <FormDataConsumer>
  {({formData, scopedFormData}) => {
      return (
    <>
      <ReferenceInput
        source="site_id"
        label="Select Site"
        reference="sites"
        basePath="sites"
        perPage={200}
        sort={{ field: 'name', order: 'ASC' }}
        filterToQuery={searchText => ({ search_name: searchText })}
        allowEmpty={false}
        validate={required()}
        helperText="Select the site this ad unit belongs to."
        style={{width: '50%'}}
        >
        <AutocompleteInput label="Select Site" optionText={(rec) => rec && rec.name ? `${rec.name} (${rec.publisher_name})` : ''} optionValue="code" 
          helperText="Select the site this ad unit belongs to."
          style={{width: '50%'}}/>
      </ReferenceInput>
    <TextInput source="adunit_code" label="Code" validate={required()} style={{width: '30%'}}/>
    <br/>
    <TextInput source="adunit" label="Ad Unit" validate={required()} style={{width: '50%'}}/>
    <BooleanInput source="isVideo" label='Is Video Unit' fullWidth/>
    <div style={{display: 'flex', gap: '20px' }}>
        <div style={{width: '50%'}}>
            <h4>Banner Ad Sizes</h4>
            <div style={{border: '1px solid white', padding: '15px', display: 'flex', flexDirection: 'column' }}>
                <div style={{display: 'flex', width: '100%', gap: '15px'}}>
                    <TextInput source={`sizeConfig[2].label`} defaultValue='desktop' style={{display: 'none'}}/>
                    <TextInput source={`sizeConfig[2].minViewPort`} defaultValue={'[992,0]'} style={{display: 'none'}}/>
                    <SelectArrayInput source={`sizeConfig[2].sizes`} choices={getAdSizeChoices('Desktop')} label='Desktop Ad Sizes' resettable fullWidth/>
                </div>
                <div style={{display: 'flex', width: '100%', gap: '15px'}}>
                    <TextInput source={`sizeConfig[0].label`} defaultValue='mobile' style={{display: 'none'}}/>
                    <TextInput source={`sizeConfig[0].minViewPort`} defaultValue={'[0,0]'} style={{display: 'none'}}/>
                    <SelectArrayInput source={`sizeConfig[0].sizes`} choices={getAdSizeChoices('Mobile')} label='Mobile Ad Sizes' resettable fullWidth/>
                </div>
                <div style={{display: 'flex', width: '100%', gap: '15px'}}>
                    <TextInput source={`sizeConfig[1].label`} defaultValue='tablet' style={{display: 'none'}}/>
                    <TextInput source={`sizeConfig[1].minViewPort`} defaultValue={'[767,0]'} style={{display: 'none'}}/>
                    <SelectArrayInput source={`sizeConfig[1].sizes`} choices={getAdSizeChoices('Tablet')} label='Tablet Ad Sizes' resettable fullWidth/>
                </div>
                <BooleanInput source="fluid" label='Supports fluid size' fullWidth/>
            </div>
        </div>

        <div style={{width: '50%'}}>
          <h4>Bidders</h4>
          <div style={{border: '1px solid white', padding: '15px', display: 'flex', flexDirection: 'column' }}>
              <ArrayInput source="bids" label={false}>
                  <SimpleFormIterator disableReordering>
                  <FormDataConsumer>
                      {({id, scopedFormData}) => (
                          <PrebidBidderSelector source={id} scopedFormData={scopedFormData}/>
                      )}
                  </FormDataConsumer>
                  </SimpleFormIterator>
              </ArrayInput>
          </div>
        </div>
        
    </div>
    <br/>
    <Accordion style={{width: '100%', height: '1%'}}>
      <AccordionSummary expandIcon={<ExpandMore />}>Behavior</AccordionSummary>
      <AccordionDetails>
        <div style={{display: 'flex', flexDirection: 'row', columnGap: '20px'}}>
          <div style={{display: 'flex', flexDirection: 'column', columnGap: '20px'}}>
            <div style={{width: '100%'}}>
                Lazy Load<div style={{fontSize: 'x-small'}}>The Lazy Loading behavior helps reduce page latency and ad value (CPM) because it delays making Ad Requests until the user is close by X (Margin) pixels of the Ad slot location.</div>
                <div style={{border: '1px solid white', padding: '10px', display: 'flex', flexDirection: 'column'}}>
                    <BooleanInput source="lazyLoad.enabled" label='Enable Lazy Load'/>
                    <NumberInput source="lazyLoad.margin" label='Margin' style={{width: '30%'}} helperText='This is the margin in pixels used to trigger the Ad request.'/>
                </div>
            </div>
            <div style={{width: '100%', paddingTop: '40px'}}>
                Sliding Window (per screen size)<div style={{fontSize: 'x-small'}}>The Sliding Window behavior makes the ad render in a sliding area (window), which helps increase ad viewability and in consequence ad value (CPM). Configure it per screen size; the values for the breakpoint matching the viewport are applied at serve time.</div>
                <div style={{border: '1px solid white', padding: '10px', display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'flex-start'}}>
                  <SlidingWindowFields index={2} label='Desktop'/>
                  <SlidingWindowFields index={1} label='Tablet'/>
                  <SlidingWindowFields index={0} label='Mobile'/>
                  <img src='sliding_window.png' alt='Sliding Window Preview' style={{width: '200px', height: 'auto', marginTop: '10px'}} />
                </div>
            </div>
          </div>
          <div style={{width: '50%', height: '1%'}}>
              Autorefresh<div style={{fontSize: 'x-small'}}>The Auto Refresh behavior helps in increasing inventory volume. Combined with Viewability and Lazy load, it becomes an effective way in increasing the volume of Viewable inventory.</div>
              <div style={{border: '1px solid white', padding: '15px', display: 'flex', flexDirection: 'column'}}>
                  <BooleanInput source="autorefresh.enabled" label='Enable Auto Refresh'/>
                  <div style={{gap: '10px' }}>
                    <div style={{display: 'grid'}}>
                    <div><NumberInput source="autorefresh.threshold" label='Threshold %' style={{width: '30%'}} helperText='Percentage of the Adunit visible to be considered In-View.'/></div>
                    </div>
                    <div style={{display: 'grid'}}>
                      <div><NumberInput source="autorefresh.ttv" label='Viewability time (seconds)' style={{width: '30%'}} helperText='How long the system should wait until a Refresh is triggered.'/></div>
                    </div>
                    <div style={{display: 'grid'}}>
                      <div><NumberInput source="autorefresh.stopLineItem" label='Stop Line Item' style={{width: '50%'}} helperText='This is a GAM Line Item that the system checks to determine if it should stop refresh. This is done to stop refreshing when an unfilled impression is served.'/></div>
                    </div>
                    <div style={{display: 'grid'}}>
                      <div><NumberInput source="autorefresh.impressionsLimit" label='Impressions Limit' style={{width: '30%'}} helperText='The system should only refresh un to the total "Impressions Limit".'/></div>
                    </div>
                  </div>
              </div>
          </div>
        </div>
      </AccordionDetails>
    </Accordion>
    <br/>
    <Accordion style={{width: '100%', height: '1%'}}>
      <AccordionSummary expandIcon={<ExpandMore />}>Advanced Settings</AccordionSummary>
      <AccordionDetails style={{display: 'grid'}}>
          <div style={{width: '100%'}}>
            Ad Label Settings
            <TextInput style={{marginBottom: '8px'}} source="sponsor.label" label='Ad Label' fullWidth
            defaultValue='Advertisement'/>
            <TextInput source="sponsor.style" label='Ad Label Style' minRows={3} validate={ validJSON } multiline fullWidth
            defaultValue='{"color": "gray", "fontSize": "x-small", "justifyContent": "center", "display": "grid", "paddingBottom": "5px"}'/>
          </div>
        
          <div style={{width: '100%'}}>
            Styles
            <TextInput source="style" label='Container Style' fullWidth validate={ validJSON } multiline
            defaultValue='{"justifyContent": "center", "display": "grid"}'/>
            <TextInput source="divStyle" label='Ad Div Style' fullWidth validate={ validJSON } multiline/>
          </div>
      </AccordionDetails>
    </Accordion>
  </>
  )}}
  </FormDataConsumer>
  </SimpleForm>
);

export const AdunitEdit = ({ permissions, ...props }) => {
return (
  <Edit title={<AdunitTitle />} mutationMode="pessimistic" {...props}>
    <TheForm formType="edit" permissions={permissions} {...props} />
  </Edit>
)
};

export const AdunitCreate = ({ permissions, ...props }) => {
return (
  <Create title="New Ad Unit" mutationMode="pessimistic" {...props}>
    <TheForm formType="create" permissions={permissions} {...props} />
  </Create>
)
};